import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, SearchIcon, FilterIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, ClockIcon, ChevronRightIcon, UserIcon, MapPinIcon, DollarSignIcon } from 'lucide-react';
// Mock bookings data
const bookingsData = [{
  id: 1,
  reference: 'BK-78945',
  customer: {
    name: 'Emily Wilson',
    email: 'ewilson@example.com',
    phone: '+1 (555) 789-0123'
  },
  package: 'Luxury Bali Retreat',
  destination: 'Bali, Indonesia',
  travelDates: 'Jul 20 - Jul 27, 2023',
  bookingDate: '2023-06-02T15:45:00',
  totalAmount: 3950,
  paidAmount: 1975,
  balance: 1975,
  status: 'confirmed',
  paymentStatus: 'deposit-paid',
  travelers: '2 adults',
  agent: 'David Miller'
}, {
  id: 2,
  reference: 'BK-78946',
  customer: {
    name: 'Robert Chen',
    email: 'robert.chen@example.com',
    phone: '+1 (555) 123-4567'
  },
  package: 'Japan Family Explorer',
  destination: 'Japan',
  travelDates: 'Oct 15 - Oct 28, 2023',
  bookingDate: '2023-06-12T10:30:00',
  totalAmount: 6850,
  paidAmount: 2000,
  balance: 4850,
  status: 'confirmed',
  paymentStatus: 'deposit-paid',
  travelers: '2 adults, 1 child',
  agent: 'Sarah Johnson'
}, {
  id: 3,
  reference: 'BK-78947',
  customer: {
    name: 'Michael Torres',
    email: 'mtorres@example.com',
    phone: '+1 (555) 456-7890'
  },
  package: 'European Heritage Tour',
  destination: 'Multiple (Europe)',
  travelDates: 'Sep 12 - Sep 26, 2023',
  bookingDate: '2023-06-09T14:15:00',
  totalAmount: 5450,
  paidAmount: 5450,
  balance: 0,
  status: 'confirmed',
  paymentStatus: 'paid',
  travelers: '1 adult',
  agent: 'Sarah Johnson'
}, {
  id: 4,
  reference: 'BK-78948',
  customer: {
    name: 'Jennifer Williams',
    email: 'jwilliams@example.com',
    phone: '+1 (555) 987-6543'
  },
  package: 'Caribbean Cruise Deluxe',
  destination: 'Caribbean',
  travelDates: 'Aug 5 - Aug 12, 2023',
  bookingDate: '2023-06-14T11:20:00',
  totalAmount: 3200,
  paidAmount: 1000,
  balance: 2200,
  status: 'pending-confirmation',
  paymentStatus: 'deposit-paid',
  travelers: '2 adults',
  agent: 'Sarah Johnson'
}, {
  id: 5,
  reference: 'BK-78949',
  customer: {
    name: 'David Miller',
    email: 'dmiller@example.com',
    phone: '+1 (555) 234-5678'
  },
  package: 'New Zealand Adventure',
  destination: 'New Zealand',
  travelDates: 'Nov 5 - Nov 15, 2023',
  bookingDate: '2023-06-01T09:45:00',
  totalAmount: 4800,
  paidAmount: 0,
  balance: 4800,
  status: 'cancelled',
  paymentStatus: 'refunded',
  travelers: '2 adults',
  agent: 'David Miller'
}];
export const BookingsModule = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  // Filter bookings based on search term and filters
  const filteredBookings = bookingsData.filter(booking => {
    // Apply status filter
    if (statusFilter !== 'all' && booking.status !== statusFilter) return false;
    // Apply payment filter
    if (paymentFilter !== 'all' && booking.paymentStatus !== paymentFilter) return false;
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return booking.customer.name.toLowerCase().includes(term) || booking.reference.toLowerCase().includes(term) || booking.package.toLowerCase().includes(term) || booking.destination.toLowerCase().includes(term);
    }
    return true;
  });
  const getStatusBadge = status => {
    switch (status) {
      case 'confirmed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-667">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-668" />
            Confirmed
          </span>;
      case 'pending-confirmation':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-669">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-670" />
            Pending
          </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-671">
            <XCircleIcon className="h-3 w-3 mr-1" data-id="element-672" />
            Cancelled
          </span>;
      default:
        return null;
    }
  };
  const getPaymentStatusBadge = status => {
    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-673">
            Paid
          </span>;
      case 'deposit-paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-674">
            Deposit Paid
          </span>;
      case 'unpaid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-675">
            Unpaid
          </span>;
      case 'refunded':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-676">
            Refunded
          </span>;
      default:
        return null;
    }
  };
  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const handleBookingClick = id => {
    navigate(`/bookings/${id}`);
  };
  return <div className="space-y-6" data-id="element-677">
      <div className="flex justify-between items-center" data-id="element-678">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-679">Bookings</h1>
        <div className="flex space-x-2" data-id="element-680">
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" onClick={() => navigate('/enquiries')} data-id="element-681">
            View Enquiries
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => navigate('/products')} data-id="element-682">
            Create New Booking
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex space-x-4" data-id="element-683">
        <div className="relative flex-1" data-id="element-684">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-685">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-686" />
          </div>
          <input type="text" placeholder="Search bookings..." className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-687" />
        </div>
        <select className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-688">
          <option value="all" data-id="element-689">All Statuses</option>
          <option value="confirmed" data-id="element-690">Confirmed</option>
          <option value="pending-confirmation" data-id="element-691">Pending</option>
          <option value="cancelled" data-id="element-692">Cancelled</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)} data-id="element-693">
          <option value="all" data-id="element-694">All Payments</option>
          <option value="paid" data-id="element-695">Paid</option>
          <option value="deposit-paid" data-id="element-696">Deposit Paid</option>
          <option value="unpaid" data-id="element-697">Unpaid</option>
          <option value="refunded" data-id="element-698">Refunded</option>
        </select>
        <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-699">
          <FilterIcon className="h-4 w-4 mr-2" data-id="element-700" />
          More Filters
        </button>
      </div>

      {/* Bookings list */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-701">
        {filteredBookings.length === 0 ? <div className="p-8 text-center" data-id="element-702">
            <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" data-id="element-703" />
            <h3 className="text-lg font-medium text-gray-800 mb-2" data-id="element-704">
              No bookings found
            </h3>
            <p className="text-gray-500 mb-4" data-id="element-705">
              Try adjusting your search or filters
            </p>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => {
          setSearchTerm('');
          setStatusFilter('all');
          setPaymentFilter('all');
        }} data-id="element-706">
              Reset Filters
            </button>
          </div> : <div className="overflow-x-auto" data-id="element-707">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-708">
              <thead className="bg-gray-50" data-id="element-709">
                <tr data-id="element-710">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-711">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-712">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-713">
                    Package
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-714">
                    Travel Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-715">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-716">
                    Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-717">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-718">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-719">
                {filteredBookings.map(booking => <tr key={booking.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleBookingClick(booking.id)} data-id="element-720">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-721">
                      <div className="text-sm font-medium text-gray-900" data-id="element-722">
                        {booking.reference}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-723">
                        {formatDate(booking.bookingDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-724">
                      <div className="flex items-center" data-id="element-725">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-726">
                          <UserIcon className="h-5 w-5" data-id="element-727" />
                        </div>
                        <div className="ml-4" data-id="element-728">
                          <div className="text-sm font-medium text-gray-900" data-id="element-729">
                            {booking.customer.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-730">
                            {booking.travelers}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-731">
                      <div className="text-sm text-gray-900" data-id="element-732">
                        {booking.package}
                      </div>
                      <div className="flex items-center text-xs text-gray-500" data-id="element-733">
                        <MapPinIcon className="h-3 w-3 mr-1" data-id="element-734" />
                        {booking.destination}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-735">
                      <div className="flex items-center" data-id="element-736">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-737" />
                        <span className="text-sm text-gray-900" data-id="element-738">
                          {booking.travelDates}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-739">
                      {getStatusBadge(booking.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-740">
                      {getPaymentStatusBadge(booking.paymentStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-741">
                      <div className="flex items-center" data-id="element-742">
                        <DollarSignIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-743" />
                        <div data-id="element-744">
                          <div className="text-sm font-medium text-gray-900" data-id="element-745">
                            ${booking.totalAmount}
                          </div>
                          {booking.balance > 0 && <div className="text-xs text-gray-500" data-id="element-746">
                              Balance: ${booking.balance}
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-747">
                      <button className="text-blue-600 hover:text-blue-900" data-id="element-748">
                        <ChevronRightIcon className="h-5 w-5" data-id="element-749" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
};